import { FC, PropsWithChildren, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router";

import { useAlert } from "capitalrx-components-2.0/lib/general";
import type { AlertType } from "capitalrx-components-2.0/lib/general";
import { BreadcrumbItem, Breadcrumbs } from "capitalrx-shell";
import { useSetLayout } from "capitalrx-shell";

import { PageAlert } from "@/App/components";
import { ANALYTIC_EVENTS, logEvent } from "@/App/utils/analytics";

import { PageAlertState } from "../../../types/appTypes/general";
import { RootState } from "../../reducers";
import { appRoutes } from "../../routes/routes";

/**
 * The legacy content layout component properties.
 */
export interface LegacyContentLayoutProps {
  /**
   * Indicates whether the content should be displayed fullscreen.
   */
  readonly fullscreen?: boolean;
}

/**
 * The legacy breadcrumb component.
 */
const LegacyBreadcrumb: FC = (): JSX.Element => {
  const { pathname } = useLocation();
  const breadcrumbClickHandler = useCallback((breadcrumb: string): void => {
    logEvent(ANALYTIC_EVENTS.MAIN_MENU.breadcrumb, { breadcrumb });
  }, []);

  const crumbs = appRoutes.filter((route) => !!route.breadcrumb && !!matchPath({ path: route.path, end: false }, pathname));

  if (crumbs.length === 0) return <></>;

  return (
    <Breadcrumbs>
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        const title = typeof crumb.breadcrumb === "string" ? crumb.breadcrumb : crumb.pageTitle;
        return (
          <BreadcrumbItem
            key={crumb.path}
            title={title}
            path={isLast ? undefined : crumb.path}
            onClick={isLast ? undefined : (): void => breadcrumbClickHandler(title)}
          />
        );
      })}
    </Breadcrumbs>
  );
};

/**
 * The legacy content layout.
 * @deprecated To be removed when legacy routes are removed.
 */
export const LegacyContentLayout: FC<PropsWithChildren<LegacyContentLayoutProps>> = ({
  fullscreen = false,
  children,
}: PropsWithChildren<LegacyContentLayoutProps>): JSX.Element => {
  const alertData = useSelector<RootState, PageAlertState>((state) => state.pageAlert);
  const showAlert = useAlert();
  const { hidePageAlert } = PageAlert;
  const dispatch = useDispatch();
  const location = useLocation();
  const { PageAlertContainer } = PageAlert;

  // Marshal the legacy alerts to new alerts.
  useEffect(() => {
    if (!alertData) {
      return;
    }

    const { message, type: legacyType, timeout = 5000, show } = alertData;

    if (!show) {
      return;
    }

    let type: AlertType = "error";
    switch (legacyType) {
      case "success":
        type = "success";
        break;
      case "warning":
        type = "warning";
        break;
      case "error":
        type = "error";
        break;
      default:
        type = "informational";
        break;
    }

    showAlert({ message, type, style: "banner", timeout });
  }, [alertData]);

  const setLayout = useSetLayout();

  useEffect(() => {
    setLayout(fullscreen ? "fullscreen" : "auto");
  }, [fullscreen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(hidePageAlert());
    }, 1500);
    return () => clearTimeout(timer);
  }, [location, dispatch]);

  return (
    <>
      <PageAlertContainer
        iconClass={alertData.iconClass}
        message={alertData.message}
        show={alertData.show}
        timeout={alertData.timeout ?? 5000}
        type={alertData.type}
      />
      {!fullscreen && <LegacyBreadcrumb />}
      {children}
    </>
  );
};
